import { ArrowRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_tervalia-atelier/artifacts/ahsbvyl1_hlavni_obrazek.png";

export default function Hero() {
  const { t } = useI18n();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-end overflow-hidden bg-[#f9f8f5]"
    >
      {/* Background image */}
      <img
        src={HERO_IMAGE}
        alt=""
        fetchPriority="high"
        loading="eager"
        decoding="async"
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Cream overlay — brighter, softer editorial feel */}
      <div
        className="absolute inset-0 bg-[#f9f8f5]/45"
        aria-hidden="true"
      />
      {/* Extra readability veil on the left where headline sits */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-[#f9f8f5]/65 via-[#f9f8f5]/25 to-transparent"
        aria-hidden="true"
      />
      {/* Bottom gradient fade */}
      <div
        className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-[#f9f8f5]"
        aria-hidden="true"
      />

      <div className="relative w-full max-w-[1280px] mx-auto px-6 md:px-10 pb-24 md:pb-32 pt-40 md:pt-48">
        <div className="max-w-[820px]">
          <div className="eyebrow mb-8 tv-hero-fade" style={{ animationDelay: "80ms" }} data-testid="hero-eyebrow">
            {t.hero.eyebrow}
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif-hero text-[#252525] text-[42px] leading-[1.05] sm:text-[56px] md:text-[74px] lg:text-[88px] tracking-[-0.01em] tv-hero-fade"
            style={{ animationDelay: "180ms" }}
          >
            <span className="block">{t.hero.title_line1}</span>
            <span className="block italic text-[#4a4a45] font-light">{t.hero.title_line2}</span>
            <span className="block">{t.hero.title_line3}</span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-8 max-w-[620px] text-[15px] md:text-base leading-relaxed text-[#4a4a45] tv-hero-fade"
            style={{ animationDelay: "300ms" }}
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 tv-hero-fade" style={{ animationDelay: "420ms" }}>
            <button
              data-testid="hero-cta-services"
              onClick={() => scrollTo("services")}
              className="tv-btn tv-btn-primary"
            >
              {t.hero.cta_primary}
              <ArrowRight size={14} strokeWidth={1.5} className="arrow" />
            </button>
            <button
              data-testid="hero-cta-gallery"
              onClick={() => scrollTo("gallery")}
              className="tv-btn tv-btn-outline-light"
            >
              {t.hero.cta_secondary}
              <ArrowRight size={14} strokeWidth={1.5} className="arrow" />
            </button>
          </div>
        </div>

        {/* Founded label bottom right */}
        <div className="hidden md:block absolute right-10 bottom-24 text-right tv-hero-fade" style={{ animationDelay: "540ms" }}>
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#6f6f6a]">
            {t.hero.founded_label}
          </div>
          <div className="mt-1 font-serif italic text-[#4a4a45] text-[15px] max-w-[240px]">
            {t.hero.founded_sub}
          </div>
        </div>
      </div>
    </section>
  );
}
